"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.captureAttribution = captureAttribution;
exports.persistProfileUuid = persistProfileUuid;
exports.markSpineSpent = markSpineSpent;
exports.resolveProfileUuid = resolveProfileUuid;
/**
 * Order Form Events v4 — marketing attribution capture.
 * See docs/ORDER_FORM_EVENTS_V4.md §6.1 and docs/ATTRIBUTION_CAPTURE.md.
 *
 * Where the params come from, in priority order:
 *   1. this URL's query string           — the freshest touch, always wins
 *   2. the shared `w3_attribution` cookie — written on `.wire3.com` by the WordPress plugin
 *      (wordpress/functions.php) and by OrderFormRoutes, so a tag that landed on
 *      wire3.com/availability/ still reaches the form when the click-through drops the query
 *      string. hub.wire3.com and wire3.com are the same *site*, so this cookie is first-party
 *      inside the iframe embed and survives ITP/CHIPS partitioning.
 *   3. the referring URL's query string   — last resort, only when the browser sends a full
 *      Referer (most send origin only), for a parent page that neither decorated nor cookied.
 *   4. the legacy `w3_first_touch` / `w3_last_touch` cookies — visitors who landed before the
 *      shared store existed. Read-only now: folded into the store, then cleared. They duplicated the
 *      store's two slots and at ~4KB each were what put the `Cookie` header over nginx's 8KB limit.
 *
 * The jar holds ONE cookie: a write-once `firstTouch`, a `lastTouch` overwritten on every qualifying
 * landing, and the params union. Mongo is the history — the portal appends every touch to
 * `attribution.touch` — so nothing else needs to accumulate client-side.
 *
 * What we hand to the funnel + ingest is the envelope HQ asked for:
 *   { firstTouch: {at, page, params}, lastTouch: {at, page, params}, params: {union of all} }
 * `params` is the running UNION across visits — HQ joins on attribution.params.w3_campaign.
 *
 * Cookies: the per-touch mirrors keep SameSite=None;Secure (they predate the shared store and are
 * read by the server mirror); the shared store is SameSite=Lax;Secure per the hand-off contract.
 * Never throws — attribution must not be able to break the form.
 */
const Utils_1 = require("./Utils");
const Attribution_1 = require("./types/Attribution");
const FIRST_TOUCH_COOKIE = "w3_first_touch";
const LAST_TOUCH_COOKIE = "w3_last_touch";
const FIRST_TOUCH_DAYS = 365;
const LAST_TOUCH_DAYS = 30;
/** The store shared with wire3.com. Same name/shape on both sides — do not rename. */
const STORE_COOKIE = "w3_attribution";
const STORE_DAYS = 90;
/**
 * Measured on the ENCODED value — that is what the browser stores and what rides back in the `Cookie`
 * header. JSON percent-encodes badly (`{`, `"`, `:`, `,` cost 3 bytes each), so a raw-length cap of
 * 3,500 let the value reach 4.5KB+ on the wire.
 *
 * Sits under the tighter of the two real limits: Chromium drops a cookie whose name + value passes
 * 4,096 (`kMaxCookieNamePlusValueSize`); nginx 400s a header line over 8KB. `w3_attribution=` is 15
 * bytes, so 4,000 keeps as much attribution as the browser allows with headroom to spare.
 */
const STORE_MAX_BYTES = 4000;
/**
 * The form a touch takes INSIDE a cookie. `landingUrl` / `referrer` are the bulk of the record (a
 * rep's autofill link is ~930 chars, and the referring URL repeats the tags) and the nested `params`
 * duplicates the flat keys.
 *
 * None of it is lost at ingest: the touch this landing sends is built fresh from `location.href` /
 * `document.referrer` below, and the portal is the system of record — it writes
 * `attribution.firstTouch` write-once and appends every touch to `attribution.touch`, both keeping
 * the field. `rehydrateTouch()` rebuilds `params` on the way out so HQ's `{at,page,params}` contract
 * still holds for a touch that came back from a cookie.
 *
 * It also stops a 365-day cookie from carrying an autofill link's `address` / `firstName` / `email` /
 * `phone` — the exact PII the tracked-param allow-list exists to exclude.
 */
function cookieTouch(touch) {
    if (!touch)
        return null;
    const copy = { ...touch };
    delete copy.landingUrl;
    delete copy.referrer;
    delete copy.params;
    return copy;
}
/** Rebuild the nested `params` bag a cookie-stored touch left behind. */
function rehydrateTouch(touch) {
    if (!touch)
        return null;
    if (touch.params && Object.keys(touch.params).length)
        return touch;
    const params = (0, Attribution_1.pickTrackedParams)(touch);
    return Object.keys(params).length ? { ...touch, params } : touch;
}
function setNoneSecureCookie(name, value, expDays) {
    const date = new Date();
    date.setTime(date.getTime() + expDays * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${date.toUTCString()};path=/;SameSite=None;Secure`;
}
function decodeTouch(raw) {
    if (!raw)
        return null;
    try {
        return rehydrateTouch(JSON.parse(decodeURIComponent(escape(atob(raw)))));
    }
    catch {
        return null;
    }
}
/**
 * Read one cookie WITHOUT decoding the whole jar first (getCookie() does, which would corrupt a
 * JSON value whose params contain an encoded `;`).
 */
function readRawCookie(name) {
    try {
        const match = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "=([^;]*)"));
        return match ? decodeURIComponent(match[1]) : "";
    }
    catch {
        return "";
    }
}
/** The shared `.wire3.com` store, or an empty one. Tolerates anything in the cookie. */
function readStore() {
    const empty = { firstTouch: null, lastTouch: null, params: {} };
    const raw = readRawCookie(STORE_COOKIE);
    if (!raw)
        return empty;
    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object")
            return empty;
        return {
            firstTouch: rehydrateTouch(parsed.firstTouch || null),
            lastTouch: rehydrateTouch(parsed.lastTouch || null),
            params: (0, Attribution_1.pickTrackedParams)(parsed.params || {}),
        };
    }
    catch {
        return empty;
    }
}
/**
 * Write the shared store on the widest domain we own, trimming per-touch context first if the
 * value would outgrow the ~4KB cookie limit (the params union is what HQ joins on, so it is last
 * to go). Off wire3.com (local dev / review apps) the cookie is written host-only.
 */
function writeStore(store) {
    // Every candidate is measured AFTER encoding — the raw length understates the cookie by 1.3-3x.
    const wire = (payload) => encodeURIComponent(payload).length;
    const candidates = [
        // the slim cookie shape: no landingUrl/referrer, no nested params — see cookieTouch()
        () => JSON.stringify({ firstTouch: cookieTouch(store.firstTouch), lastTouch: cookieTouch(store.lastTouch), params: store.params }),
        // drop the touches; the params union is what HQ joins on, so it is last to go
        () => JSON.stringify({ firstTouch: null, lastTouch: null, params: store.params }),
    ];
    let payload = "";
    for (const build of candidates) {
        payload = build();
        if (wire(payload) <= STORE_MAX_BYTES)
            break;
        payload = "";
    }
    if (!payload)
        return false;
    const date = new Date();
    date.setTime(date.getTime() + STORE_DAYS * 24 * 60 * 60 * 1000);
    const host = (location.hostname || "").toLowerCase();
    const domain = (host === "wire3.com" || host.endsWith(".wire3.com")) ? ";domain=.wire3.com" : "";
    document.cookie = `${STORE_COOKIE}=${encodeURIComponent(payload)};expires=${date.toUTCString()};path=/${domain};SameSite=Lax;Secure`;
    return true;
}
/**
 * Retire the legacy `w3_first_touch` / `w3_last_touch` mirrors. They held the same two touches the
 * store holds, so the jar carried everything twice — and at ~4KB each they were what pushed the
 * `Cookie` header past nginx's 8KB limit. captureAttribution() folds them into the store first, so
 * this only ever runs after the store is safely written. Mongo is the history: the portal appends
 * every touch to `attribution.touch`, so a cookie needs only the two live slots.
 */
function retireLegacyMirrors() {
    for (const name of [FIRST_TOUCH_COOKIE, LAST_TOUCH_COOKIE]) {
        if ((0, Utils_1.getCookie)(name))
            document.cookie = `${name}=;max-age=0;path=/;SameSite=None;Secure`;
    }
}
/**
 * Restore `landingUrl` / `referrer` on a touch that came back from a cookie but describes the landing
 * happening right now — the cookie shape drops them (see cookieTouch()), and for a first-touch that
 * ingest is the only time the portal will ever store them. Same-params is the test; ts/at/page are
 * left exactly as the stored touch had them.
 */
function enrichWithThisLanding(stored, thisTouch) {
    if (!stored)
        return stored;
    if (stored.landingUrl || stored.referrer)
        return stored;
    const a = (0, Attribution_1.touchParams)(stored), b = (0, Attribution_1.touchParams)(thisTouch);
    const keys = Object.keys(b);
    if (keys.length !== Object.keys(a).length || !keys.every((k) => a[k] === b[k]))
        return stored;
    const out = { ...stored };
    if (thisTouch.landingUrl)
        out.landingUrl = thisTouch.landingUrl;
    if (thisTouch.referrer)
        out.referrer = thisTouch.referrer;
    return out;
}
/** Tracked params off the referring URL — only ever populated when the browser sends a full Referer. */
function paramsFromReferrer() {
    try {
        if (!document.referrer)
            return {};
        const url = new URL(document.referrer);
        return (0, Attribution_1.pickTrackedParams)(url.searchParams);
    }
    catch {
        return {};
    }
}
/**
 * Resolve this page load's attribution: merge the URL, the shared store and the legacy cookies,
 * apply the first/last-touch write policy, persist, and return the envelope the funnel carries on
 * every event + ingest.
 */
function captureAttribution(urlParams) {
    const store = readStore();
    const legacyFirst = decodeTouch((0, Utils_1.getCookie)(FIRST_TOUCH_COOKIE));
    const legacyLast = decodeTouch((0, Utils_1.getCookie)(LAST_TOUCH_COOKIE));
    // this landing: the query string wins, the referring URL is the fallback
    const fromUrl = (0, Attribution_1.pickTrackedParams)(urlParams);
    const landed = (0, Attribution_1.hasTrackedParams)(fromUrl) ? fromUrl : paramsFromReferrer();
    // everything this browser has ever carried; the current landing wins a key collision
    const union = {
        ...(0, Attribution_1.touchParams)(legacyFirst),
        ...(0, Attribution_1.touchParams)(legacyLast),
        ...store.params,
        ...landed,
    };
    let firstTouch = store.firstTouch || legacyFirst;
    let lastTouch = store.lastTouch || legacyLast;
    // A landing carrying only a weak param (bare `utm`) is still persisted, but never writes a
    // touch — see hasQualifyingParams(). Anything else is a real campaign touch.
    if ((0, Attribution_1.hasQualifyingParams)(landed)) {
        const ts = new Date().toISOString();
        const thisTouch = {
            ...landed, // flat params (legacy readers)
            ts,
            at: ts, // HQ contract
            page: typeof location !== "undefined" ? location.pathname : undefined,
            params: landed, // HQ contract
            landingUrl: typeof location !== "undefined" ? location.href : undefined,
            referrer: typeof document !== "undefined" ? document.referrer : undefined,
        };
        const agent = landed["agent"] || (0, Utils_1.getCookie)("agent");
        const promo = landed["promo"] || urlParams.get("promo") || "";
        if (agent)
            thisTouch.agent = agent;
        if (promo)
            thisTouch.promo = promo;
        if (!firstTouch)
            firstTouch = thisTouch; // first-touch: WRITE ONCE, ever
        // The server's own capture (OrderFormRoutes.writeAttributionCookies) already wrote the store on
        // THIS request, in the slim cookie shape — so `store.firstTouch` above can be this very landing
        // minus its landingUrl/referrer. Put them back when the touch describes this landing: the
        // portal records `attribution.firstTouch` write-once, so this is the one and only chance to
        // send that context. Keeps the original ts/at/page — a genuine earlier first-touch is untouched
        // except for context it was already missing.
        firstTouch = enrichWithThisLanding(firstTouch, thisTouch);
        lastTouch = thisTouch; // last-touch: WRITE EVERY landing-with-params
        // per-touch mirrors (unchanged policy) …
        // the shared store is the only thing we write — it already holds both touches plus the union
        if (writeStore({ firstTouch, lastTouch, params: union }))
            retireLegacyMirrors();
    }
    else if ((0, Attribution_1.hasTrackedParams)(landed) || (Object.keys(union).length && !readRawCookie(STORE_COOKIE))) {
        // no touch to record — but keep the union (a weak-only landing, or a pre-store visitor whose
        // legacy per-touch cookies are all we have)
        if (writeStore({ firstTouch, lastTouch, params: union }))
            retireLegacyMirrors();
    }
    return { firstTouch, lastTouch, params: union };
}
/** Persist the profileUuid spine cookie client-side (long-lived), independent of the server write. */
function persistProfileUuid(uuid) {
    if (!uuid)
        return;
    setNoneSecureCookie("uuid", uuid, FIRST_TOUCH_DAYS);
}
// ── spine rotation after a completed order ────────────────────────────────────────────────────
// Mirror of the /order bundle (order-form-new/src/Attribution.ts) — keep the two in sync.
//
// The profileUuid is a per-BROWSER cookie and the portal's identity graph keys people off it.
// That is correct for a household laptop and wrong for a sales rep's tablet: reps sign up several
// neighbours back-to-back on one device, and every one of those customers inherited the same spine
// (289 devices / 1,522 orders as of the Aug 2026 audit). So the spine is retired once it has
// produced an order: flagged at `addedPayment`, rotated on the NEXT form load — this session still
// needs the original for scheduling / the FTL hand-off.
const SPINE_SPENT_COOKIE = "uuid_spent";
/** Mark the current profileUuid as having produced an order — the NEXT form load rotates it. */
function markSpineSpent() {
    setNoneSecureCookie(SPINE_SPENT_COOKIE, "1", FIRST_TOUCH_DAYS);
}
/**
 * The profileUuid this page load should use: a fresh one when the previous spine already produced
 * an order (clearing the flag), otherwise the existing cookie, otherwise a new uuid.
 */
function resolveProfileUuid() {
    const existing = (0, Utils_1.getCookie)("uuid");
    if ((0, Utils_1.getCookie)(SPINE_SPENT_COOKIE)) {
        document.cookie = `${SPINE_SPENT_COOKIE}=;max-age=0;path=/;SameSite=None;Secure`;
        return crypto.randomUUID();
    }
    return existing || crypto.randomUUID();
}
